package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.ProviderCredentialSource
import dev.lingvo.shared.ProviderReadiness
import dev.lingvo.shared.ProviderSettingsDto
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LChord
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LSettingsIcon
import dev.lingvo.site.ds.LSparkleIcon
import dev.lingvo.site.ds.LTooltip
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.chordKeys
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val EDITOR_ASSIST_PROVIDER_HEADING_ID = "editor-assist-provider-heading"

@Composable
internal fun MachineProviderSection(
    state: TranslationEditorState,
    row: KeyRow,
    enabled: List<MtProvider>,
) {
    val fallback = defaultMachineProvider(state.projectSlug, enabled) ?: return
    var selected by remember(state.projectSlug, enabled.map(MtProvider::id)) {
        mutableStateOf(fallback)
    }
    Section(
        Modifier
            .attr("data-editor-assist-section", "provider")
            .attr("aria-labelledby", EDITOR_ASSIST_PROVIDER_HEADING_ID)
            .toAttrs(),
    ) {
        // **The section's name, not a line of type.** Drawn, this heading read "Generate
        // suggestion" directly above a button reading "Generate suggestion" — the same three words
        // twice inside 130px, and a nested heading burying "Suggestions", the heading the spec says
        // owns this block. The provider bar is the quiet "generate another" affordance under the
        // stream heading, so here — and only here, where the button restates it — the section keeps
        // its accessible name and gives up the visible line. The four unavailable variants below
        // have no such button, so their heading is the only thing naming them and stays drawn.
        Span(
            LVisuallyHiddenStyle.toModifier()
                .attr("id", EDITOR_ASSIST_PROVIDER_HEADING_ID)
                .toAttrs(),
        ) {
            Text(t("editor.editor_assist_provider.generate_suggestion", "Generate suggestion"))
        }
        EditorProviderControls(
            providers = enabled,
            selected = selected,
            translating = state.panels.translatingProviders[row.key.id].orEmpty(),
            providerSettings = state.providers,
            generationStatus = { provider ->
                state.panels.generationStatuses[EditorGenerationKey(row.key.id, provider)]
            },
            onSelected = { provider ->
                selected = provider
                rememberMachineProvider(state.projectSlug, provider)
            },
            onTranslate = { provider ->
                selected = provider
                rememberMachineProvider(state.projectSlug, provider)
                state.panels.translate(row, provider)
            },
        )
    }
}

@Composable
internal fun MachineProviderUnavailableSection(
    projectSlug: String,
    canManageProviders: Boolean,
) {
    Section(
        Modifier
            .attr("data-editor-assist-section", "provider")
            .attr("aria-labelledby", EDITOR_ASSIST_PROVIDER_HEADING_ID)
            .toAttrs(),
    ) {
        AssistSectionHeading(
            t("editor.editor_assist_provider.generate_suggestion", "Generate suggestion"),
            EDITOR_ASSIST_PROVIDER_HEADING_ID,
        )
        Div(
            EditorProviderControlsStyle.toModifier()
                .attr("data-editor-provider-unavailable", "true")
                .toAttrs(),
        ) {
            Span(Modifier.attr("data-editor-provider-copy", "readiness").toAttrs()) {
                Text(
                    if (canManageProviders) {
                        t(
                            "editor.editor_assist_provider.no_translation_provider_is_ready_configure",
                            "No translation provider is ready. Configure one in Project settings before " +
                                "generating a suggestion.",
                        )
                    } else {
                        t(
                            "editor.editor_assist_provider.no_translation_provider_is_ready_ask",
                            "No translation provider is ready. Ask an owner or admin to configure one " +
                                "before generating a suggestion.",
                        )
                    },
                )
            }
            if (canManageProviders) {
                Link(
                    path = "/app/projects/$projectSlug/settings?tab=providers",
                    modifier = EditorProviderSettingsLinkStyle.toModifier()
                        .attr("data-editor-provider-settings-link", "true"),
                ) {
                    LSettingsIcon(Modifier.iconSize(IconSize.MD))
                    Text(t("editor.editor_assist_provider.open_provider_settings", "Open provider settings"))
                }
            }
        }
    }
}

/**
 * The section that replaces the provider bar for somebody who may not spend.
 *
 * Machine translation reserves the workspace's credits, so `POST /mt/translate` asks for role
 * >= ADMIN and the `mt:spend` scope. Drawing the generate button anyway and letting the route
 * refuse is the offer-then-refuse shape the system forbids; drawing nothing at all leaves a
 * translator looking at a panel that is missing a control their colleague has. So the control is
 * absent and this sentence names who holds the privilege.
 */
@Composable
internal fun MachineSpendUnavailableSection() {
    Section(
        Modifier
            .attr("data-editor-assist-section", "provider")
            .attr("aria-labelledby", EDITOR_ASSIST_PROVIDER_HEADING_ID)
            .toAttrs(),
    ) {
        AssistSectionHeading(
            t("editor.editor_assist_provider.generate_suggestion", "Generate suggestion"),
            EDITOR_ASSIST_PROVIDER_HEADING_ID,
        )
        Div(
            EditorProviderControlsStyle.toModifier()
                .attr("data-editor-provider-unavailable", "no-spend")
                .toAttrs(),
        ) {
            Span(Modifier.attr("data-editor-provider-copy", "spend").toAttrs()) {
                Text(
                    t(
                        "editor.editor_assist_provider.machine_translation_spends_this_workspaces",
                        "Machine translation spends this workspace's credits, so an owner or admin " +
                            "starts it. The suggestions above are still yours to insert.",
                    ),
                )
            }
        }
    }
}

@Composable
internal fun MachineProviderLoadUnavailableSection(
    loading: Boolean,
    onRetry: () -> Unit,
) {
    Section(
        Modifier
            .attr("data-editor-assist-section", "provider")
            .attr("aria-labelledby", EDITOR_ASSIST_PROVIDER_HEADING_ID)
            .toAttrs(),
    ) {
        AssistSectionHeading(
            t("editor.editor_assist_provider.generate_suggestion", "Generate suggestion"),
            EDITOR_ASSIST_PROVIDER_HEADING_ID,
        )
        Div(
            EditorProviderControlsStyle.toModifier()
                .attr("data-editor-provider-unavailable", "load-failed")
                .toAttrs(),
        ) {
            Span(Modifier.attr("data-editor-provider-copy", "availability").toAttrs()) {
                Text(t("editor.editor_assist_provider.translation_provider_availability_couldnt_be_loa", "Translation provider availability couldn't be loaded. Try again before generating a suggestion."))
            }
            LButton(
                label = t("editor.editor_assist_provider.retry_provider_availability", "Retry provider availability"),
                onClick = onRetry,
                modifier = Modifier.editorAssistWrappingAction(),
                variant = LButtonVariant.SECONDARY,
                size = LButtonSize.SM,
                loading = loading,
            )
        }
    }
}

@Composable
internal fun MachineProviderLoadingSection() {
    Section(
        Modifier
            .attr("data-editor-assist-section", "provider")
            .attr("aria-labelledby", EDITOR_ASSIST_PROVIDER_HEADING_ID)
            .toAttrs(),
    ) {
        AssistSectionHeading(
            t("editor.editor_assist_provider.generate_suggestion", "Generate suggestion"),
            EDITOR_ASSIST_PROVIDER_HEADING_ID,
        )
        Div(
            EditorProviderControlsStyle.toModifier()
                .attr("data-editor-provider-unavailable", "loading")
                .attr("aria-busy", "true")
                .toAttrs(),
        ) {
            Span(Modifier.attr("data-editor-provider-copy", "availability").toAttrs()) {
                Text(t("editor.editor_assist_provider.checking_translation_provider_availability", "Checking translation provider availability…"))
            }
        }
    }
}

@Composable
internal fun EditorProviderControls(
    providers: List<MtProvider>,
    selected: MtProvider,
    translating: Set<MtProvider>,
    providerSettings: List<ProviderSettingsDto> = emptyList(),
    generationStatus: (MtProvider) -> EditorGenerationStatus? = { null },
    onSelected: (MtProvider) -> Unit,
    onTranslate: (MtProvider) -> Unit,
) {
    if (providers.isEmpty()) return
    var current by remember(providers, selected) {
        mutableStateOf(selected.takeIf { it in providers } ?: providers.first())
    }
    val settings = providerSettings.firstOrNull { it.provider == current }
    val status = generationStatus(current)
    Div(
        EditorProviderControlsStyle.toModifier()
            .attr("data-editor-provider-controls", "true")
            .toAttrs(),
    ) {
        // **The prose about this connection hangs off the select, not under it.**
        //
        // "Uses Lingvo's provider connection; machine-translation credits may apply." and
        // "Generating with DeepL…" are two sentences that answer questions about the *provider*,
        // and they were two permanent lines of body text between the reader and the suggestions
        // this panel exists to show. In a tooltip they are one hover or one focus away from the
        // control they describe.
        //
        // The prose stays in the accessible tree as well, visually hidden, and that is not
        // belt-and-braces. `LTooltip` puts its `aria-describedby` on whatever its `content` slot
        // returns, and `LSelect` spends that modifier on its own field wrapper rather than on the
        // `<select>` inside it — so the description hangs off an element that never takes focus.
        // Moving copy about spent credits out of a screen reader's reach is not a layout change,
        // it is a removal, so the sentence keeps the place in the reading order it already had.
        // See the cross-file request for the parameter that would let the tooltip reach the
        // control itself and let this copy go.
        LTooltip(
            placement = PopupPlacement.Bottom,
            tooltipContent = {
                // Elements rather than bare text nodes: the tooltip body is a flex column, and two
                // adjacent text runs collapse into one anonymous item — the connection sentence and
                // the outcome would print as one run with no space between them.
                Span(Modifier.toAttrs()) { Text(providerConnectionDescription(settings)) }
                providerOutcomeDescription(status)?.let { outcome ->
                    Span(Modifier.toAttrs()) { Text(outcome) }
                }
            },
        ) { triggerModifier ->
            LSelect(
                value = current.id,
                options = providers.map { provider -> LSelectOption(provider.id, provider.displayName) },
                onValueChange = { id ->
                    providers.firstOrNull { it.id == id }?.let { provider ->
                        current = provider
                        onSelected(provider)
                    }
                },
                modifier = Modifier
                    .attr("data-editor-provider-field", "true")
                    .then(triggerModifier),
                // Deliberately not disabled while a generation runs. `beginGeneration` refuses only
                // a second call to the *same* provider, so two providers racing on one key is a
                // supported shape, and locking the select is the only thing that would stop a
                // translator starting the second one. A disabled control also drops the caret to
                // `<body>` when the chord that starts a generation fires while the select still
                // holds focus.
                label = t("editor.editor_assist_provider.provider", "Provider"),
            )
        }
        Span(
            LVisuallyHiddenStyle.toModifier()
                .attr("data-editor-provider-copy", "connection")
                .toAttrs(),
        ) {
            Text(providerConnectionDescription(settings))
        }
        providerOutcomeDescription(status)?.let { outcome ->
            Span(
                LVisuallyHiddenStyle.toModifier()
                    .attr("data-editor-provider-copy", "outcome")
                    .attr("data-editor-provider-outcome", statusKind(status))
                    .toAttrs(),
            ) {
                Text(outcome)
            }
        }
        Div(Modifier.attr("data-editor-provider-actions", "true").toAttrs()) {
            LButton(
                label = t("editor.editor_assist_provider.generate_suggestion", "Generate suggestion"),
                onClick = { onTranslate(current) },
                modifier = Modifier.editorAssistWrappingAction(),
                // **SECONDARY: the editor already has its one primary.** The action bar's
                // "Approve and next" / "Save and next" carries `data-editor-primary`, and with
                // Assist open this drew a second accent button beside it. The provider bar is the
                // quiet "generate another" affordance under the stream heading, not the way in —
                // the stream above it already holds the answer.
                variant = LButtonVariant.SECONDARY,
                size = LButtonSize.SM,
                loading = current in translating,
                leadingIcon = { LSparkleIcon() },
                accessibleLabel = tf(
                    "editor.editor_assist_provider.generate_suggestion_with_provider",
                    "Generate suggestion with {provider}",
                "provider" to current.displayName),
            )
            // One cap per key, the same as the source row's own Alt+T (EditorFieldPresentation.kt).
            // A single `LKbd("Alt+T")` drew one wide slab while the identical shortcut 600px away
            // drew two caps — both on screen at once, the product contradicting itself about what
            // a key cap is — and a screen reader read the slab out as one token.
            LChord(chordKeys("Alt T"))
        }
    }
}

private fun providerConnectionDescription(settings: ProviderSettingsDto?): String = when {
    settings == null -> tr(
        "editor.editor_assist_provider.uses_the_provider_configuration_available",
        "Uses the provider configuration available to this project.",
    )

    settings.readiness != ProviderReadiness.READY ->
        settings.unavailableReason?.takeIf(String::isNotBlank)
            ?: tr(
                "editor.editor_assist_provider.this_provider_needs_configuration",
                "This provider needs configuration before it can generate a suggestion.",
            )

    settings.credentialSource == ProviderCredentialSource.PROJECT -> tr(
        "editor.editor_assist_provider.uses_this_projects_provider_connection",
        "Uses this project's provider connection; provider usage may apply.",
    )

    settings.credentialSource == ProviderCredentialSource.WORKSPACE -> tr(
        "editor.editor_assist_provider.uses_the_workspace_provider_connection",
        "Uses the workspace provider connection; provider usage may apply.",
    )

    settings.credentialSource == ProviderCredentialSource.PLATFORM -> tr(
        "editor.editor_assist_provider.uses_lingvos_provider_connection",
        "Uses Lingvo's provider connection; machine-translation credits may apply.",
    )

    else -> tr(
        "editor.editor_assist_provider.uses_the_provider_configuration_available",
        "Uses the provider configuration available to this project.",
    )
}

private fun providerOutcomeDescription(status: EditorGenerationStatus?): String? = when (status) {
    null -> null
    is EditorGenerationStatus.Running -> trf(
        "editor.editor_assist_provider.generating_with_provider",
        "Generating with {provider}…",
        "provider" to status.claim.provider.displayName,
    )

    is EditorGenerationStatus.Succeeded -> trf(
        "editor.editor_assist_provider.provider_suggestion_is_ready",
        "{provider} suggestion is ready.",
        "provider" to status.claim.provider.displayName,
    )

    is EditorGenerationStatus.NoResult -> status.message
    is EditorGenerationStatus.Failed -> status.message
}

private fun statusKind(status: EditorGenerationStatus?): String = when (status) {
    null -> "idle"
    is EditorGenerationStatus.Running -> "running"
    is EditorGenerationStatus.Succeeded -> "succeeded"
    is EditorGenerationStatus.NoResult -> "no-result"
    is EditorGenerationStatus.Failed -> "failed"
}

/** The provider used by both `Alt+T` and the Assist panel's single Generate suggestion action. */
internal fun defaultMachineProvider(projectSlug: String, enabled: List<MtProvider>): MtProvider? {
    val remembered = runCatching { window.localStorage.getItem(machineProviderMemoryKey(projectSlug)) }
        .getOrNull()
        ?.let { id -> enabled.firstOrNull { it.id == id } }
    return remembered ?: enabled.firstOrNull()
}

internal fun rememberMachineProvider(projectSlug: String, provider: MtProvider) {
    runCatching { window.localStorage.setItem(machineProviderMemoryKey(projectSlug), provider.id) }
}

private fun machineProviderMemoryKey(projectSlug: String) = "lingvo:editor:mt-provider:$projectSlug"
